import {
  CalendarDays,
  Car,
  ClipboardCheck,
  FileSpreadsheet,
  FileText,
  Landmark,
  Wallet,
  MessageCircle,
  MessagesSquare,
  Users,
  UserRound,
  Globe,
  HelpCircle,
  Home,
  Inbox,
  LayoutDashboard,
  Settings,
  Sparkles,
  Network,
  HeartHandshake,
  Wrench,
  PartyPopper,
  type LucideIcon,
} from 'lucide-react';
import type { Papel, Recurso } from '@erp/shared';

export interface ItemMenu {
  para: string;
  rotulo: string;
  icone: LucideIcon;
  papeis: Papel[];
  grupo: 'principal' | 'vendas' | 'estoque' | 'gestao' | 'rodape';
  /** aparece na barra inferior do celular; o resto vai para "Mais" */
  celular?: boolean;
  /** some do menu quando o plano não tem o recurso */
  recurso?: Recurso;
}

/** Cada papel vê só o que usa (docs/00). A mesma lista alimenta o menu lateral e a barra do celular. */
export const MENU: ItemMenu[] = [
  {
    para: '/',
    rotulo: 'Painel',
    icone: LayoutDashboard,
    papeis: ['dono', 'gerente', 'financeiro'],
    grupo: 'principal',
    celular: true,
  },
  {
    para: '/',
    rotulo: 'Início',
    icone: Home,
    papeis: ['vendedor'],
    grupo: 'principal',
    celular: true,
  },
  {
    para: '/estoque',
    rotulo: 'Estoque',
    icone: Car,
    papeis: ['dono', 'gerente', 'vendedor', 'financeiro'],
    grupo: 'estoque',
    celular: true,
  },
  {
    para: '/vistorias',
    rotulo: 'Vistorias',
    icone: ClipboardCheck,
    papeis: ['dono', 'gerente', 'vendedor'],
    grupo: 'estoque',
  },
  {
    para: '/oficina',
    rotulo: 'Garantia e serviços',
    icone: Wrench,
    papeis: ['dono', 'gerente', 'vendedor'],
    grupo: 'estoque',
  },
  {
    para: '/leads',
    rotulo: 'Leads',
    icone: Inbox,
    papeis: ['dono', 'gerente', 'vendedor'],
    grupo: 'vendas',
    celular: true,
  },
  {
    para: '/atendimento',
    rotulo: 'Conversas',
    icone: MessageCircle,
    papeis: ['dono', 'gerente', 'vendedor'],
    grupo: 'vendas',
    celular: true,
    recurso: 'central_atendimento',
  },
  {
    para: '/clientes',
    rotulo: 'Clientes',
    icone: UserRound,
    papeis: ['dono', 'gerente', 'vendedor'],
    grupo: 'vendas',
  },
  {
    para: '/agenda',
    rotulo: 'Agenda',
    icone: CalendarDays,
    papeis: ['dono', 'gerente', 'vendedor'],
    grupo: 'vendas',
  },
  {
    para: '/eventos',
    rotulo: 'Eventos e feirões',
    icone: PartyPopper,
    papeis: ['dono', 'gerente', 'vendedor'],
    grupo: 'vendas',
  },
  {
    para: '/financiamento',
    rotulo: 'Financiamento',
    icone: Landmark,
    papeis: ['dono', 'gerente', 'vendedor', 'financeiro'],
    grupo: 'vendas',
    recurso: 'financeiro_completo',
  },
  {
    para: '/financeiro',
    rotulo: 'Financeiro',
    icone: Wallet,
    papeis: ['dono', 'financeiro'],
    grupo: 'gestao',
    celular: true,
  },
  {
    para: '/fiscal',
    rotulo: 'Fiscal e documentos',
    icone: FileText,
    papeis: ['dono', 'gerente', 'financeiro'],
    grupo: 'gestao',
    recurso: 'nfe',
  },
  {
    para: '/relatorios',
    rotulo: 'Relatórios',
    icone: FileSpreadsheet,
    papeis: ['dono', 'gerente', 'financeiro'],
    grupo: 'gestao',
  },
  {
    para: '/chat',
    rotulo: 'Chat da equipe',
    icone: MessagesSquare,
    papeis: ['dono', 'gerente', 'vendedor'],
    grupo: 'gestao',
  },
  {
    para: '/equipe',
    rotulo: 'Equipe',
    icone: Users,
    papeis: ['dono', 'gerente', 'vendedor', 'financeiro'],
    grupo: 'gestao',
  },
  {
    para: '/site',
    rotulo: 'Site e anúncios',
    icone: Globe,
    papeis: ['dono', 'gerente'],
    grupo: 'estoque',
  },
  {
    para: '/inteligencia',
    rotulo: 'Inteligência',
    icone: Sparkles,
    papeis: ['dono', 'gerente', 'financeiro'],
    grupo: 'principal',
  },
  {
    para: '/rede',
    rotulo: 'Rede de lojas',
    icone: Network,
    papeis: ['dono', 'gerente'],
    grupo: 'estoque',
  },
  {
    para: '/inteligencia',
    rotulo: 'Pós-venda',
    icone: HeartHandshake,
    papeis: ['vendedor'],
    grupo: 'vendas',
    recurso: 'pos_venda',
  },
  {
    para: '/ajuda',
    rotulo: 'Ajuda',
    icone: HelpCircle,
    papeis: ['dono', 'gerente', 'vendedor', 'financeiro'],
    grupo: 'rodape',
  },
  {
    para: '/ajustes',
    rotulo: 'Ajustes',
    icone: Settings,
    papeis: ['dono', 'financeiro'],
    grupo: 'rodape',
  },
  {
    para: '/ajustes/conta',
    rotulo: 'Minha conta',
    icone: UserRound,
    papeis: ['gerente', 'vendedor'],
    grupo: 'rodape',
  },
];

export const ROTULO_GRUPO = {
  principal: '',
  vendas: 'Vendas',
  estoque: 'Estoque e anúncios',
  gestao: 'Gestão',
  rodape: '',
} as const;

/** Ordem dos grupos no menu: o dia a dia primeiro (vender), depois o carro, depois a gestão. */
export const ORDEM_GRUPOS = ['principal', 'vendas', 'estoque', 'gestao'] as const;

export const menuDoPapel = (papel: Papel, recursos: readonly string[] = []) =>
  MENU.filter((m) => m.papeis.includes(papel) && (!m.recurso || recursos.includes(m.recurso)));

/** Quem pode cadastrar carro (botão "+ Novo"). */
export const podeCadastrar = (papel: Papel) => papel === 'dono' || papel === 'gerente';
